import SearchButton from "../../../components/SearchButton";
import RefreshButton from "../../../components/RefreshButton";
import { BarsArrowUpIcon } from "@heroicons/react/24/solid";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import { Disclosure } from "@headlessui/react";
import { useRecoilState, useRecoilValue } from "recoil";
import { accountAtom } from "../../Login/atoms/AccountAtom";
import { useGetCarrierNameList } from "../Carrier/hooks/useGetCarrierNameList";
import { useStates } from "../hooks/useStates";
import { useGetActiveTargetSystem } from "./hooks/useGetActiveTargetSystem";
import { officeSearchData } from "./atoms/atoms";
import useOfficeStatus from "../../Utilities/Office/OfficeStatus/hooks/useOfficeStatus";
import useOfficeType from "../../Utilities/Office/OfficeType/hooks/useOfficeType";
import { useGetActiveGLEntity } from "./hooks/useGetActiveGLEntity";
import { useGetActiveChannel } from "./hooks/useGetActiveChannel";
import SelectHookFormMulti from "../../../components/SelectHookFormMulti";
import { useSFGetOffice } from "./hooks/useSFGetOffice";
import { useGetActiveOfficeBrand } from "./hooks/useGetActiveOfficeBrand";
import { useOfficeCities } from "./hooks/useGetOfficeCities";
import { useProducerCodes } from "./hooks/useGetProducerCodesList";
import { useOfficePhones } from "./hooks/useGetOfficePhones";

const TableFilters = ({
  filterOfficeSettings,
  setFilterStatus,
  setFilteredOfficeData,
  setIsSearching,
  filter,
  officeDistrictCodes,
  officeIsLoading,
}) => {
  const [searchInputsData, setSearchInputsData] =
    useRecoilState(officeSearchData);
  const user = useRecoilValue(accountAtom);

  const { data: carrierNames, isLoading: carrierLoading } =
    useGetCarrierNameList();
  const carriers =
    carrierNames &&
    carrierNames.map((x) => {
      return { label: x.carrierName, value: x.masterCarrierId };
    });

  const { data: statesList, isLoading: stateLoading } = useStates();

  const states =
    statesList &&
    statesList.map((x) => {
      return {
        label: x.state,
        value: x.state,
        stateCode: x.stateCode,
      };
    });

  // Combination of Brand Name and Brand ID
  const { data: officeBdata, isLoading: officeBLoading } =
    useGetActiveOfficeBrand();
  const superUser = user.isFranchise && user.isNonFranchise && !user.isMexico;
  const isMexico = !user.isFranchise && !user.isNonFranchise && user.isMexico;

  const officeBrands = officeBdata?.filter((x) => {
    if (superUser) {
      return x.usedInMexico !== true;
    } else if (isMexico) {
      return x.usedInMexico === true;
    } else {
      return x.status === 1;
    }
  });

  const activeOfficeBrand = [
    ...new Map(
      officeBrands?.map(item => [
        `${item.brandName} - ${item.state}`,
        item,
      ]) ?? []
    ).values(),
  ].map(resData => ({
    label: `${resData.brandName} - ${resData.state}`,
    value: `${resData.brandName} - ${resData.state}`,
    brandName: resData.brandName,
    state: resData.state,
    dba: resData.dba,
    usedInMexico: resData.usedInMexico,
  }));

  const activeTargetSystem = useGetActiveTargetSystem()?.sort((a, b) =>
    a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1
  );

  const { data: glEntities, isLoading: isGlEntitiesLoading } =
    useGetActiveGLEntity();

  const glEntityOpt = glEntities?.map((y) => {
    return {
      label: y.glentityName,
      value: y.glnumber,
    };
  });

  const refreshOfficeTable = () => {
    setFilteredOfficeData([]);
    setFilterStatus(false);
    setIsSearching(false);
    setSearchInputsData({
      ...searchInputsData,
      OfficeId: "",
      OfficeCode: "",
      OfficeCodeId: null,
      OfficeName: "",
      OfficeNameId: null,
      StoreNumber: "",
      OfficeStoreNumberId: null,
      GLBranch: null,
      SelectedGLBranch: null,
      AgencyName: "",
      Address: "",
      City: null,
      ZipCode: "",
      State: null,
      SelectedState: null,
      TargetSystem: null,
      SelectedTargetSystem: null,
      ProducerCode: null,
      Brand: null,
      Carriers: [],
      SelectedCarrier: null,
      IsFranchise: null,
      IsConfie: false,
      OfficeStatus: "",
      OfficeStatusId: null,
      OfficeTypeId: null,
      SelectedOfficeStatus: null,
      OfficeType: "",
      SelectedOfficeType: null,
      Channel: null,
      ChannelId: null,
      SelectedChannel: null,
      BrandSelected: null,
      SelectedCity: null,
      SelectedProducerCode: null,
      Phone: null,
      SelectedPhone: null,
      SelectedDistrictCode: null,
      DistrictCode: "",
    });
  };

  const { data: officeStatus } = useOfficeStatus();
  const officeStatusOpt =
    officeStatus &&
    officeStatus.data?.map((x) => {
      return {
        label: x.status,
        value: x.officeStatusId,
      };
    });

  const { data: channelData, isLoading: isChannelLoading } =
    useGetActiveChannel();
  const activeChannel = channelData
    ?.filter((x) => x.isActive === true)
    .map((c) => {
      return {
        label: c.channelName,
        value: c.channelId,
      };
    })
    ?.sort((a, b) => (a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1));

  const officeTypes = useOfficeType();
  const officeTypeOpt =
    user.isFranchise && user.isNonFranchise
      ? officeTypes &&
        officeTypes.data?.map((x) => {
          return {
            label: x.officeTypeName,
            value: x.officeTypeId,
          };
        })
      : user.isFranchise && !user.isNonFranchise
      ? officeTypes &&
        officeTypes.data
          ?.filter((x) => x.officeTypeName?.toLowerCase() === "franchise")
          ?.map((x) => {
            return {
              label: x.officeTypeName,
              value: x.officeTypeId,
            };
          })
      : officeTypes &&
        officeTypes.data
          ?.filter((x) => x.officeTypeName?.toLowerCase() !== "franchise")
          ?.map((x) => {
            return {
              label: x.officeTypeName,
              value: x.officeTypeId,
            };
          });
  const { data: agencies } = useSFGetOffice();
  const sfOfficeNameOpt = agencies
    ?.filter(
      (x) =>
        x.officeName !== null &&
        x.officeName !== undefined &&
        x.officeName !== "" &&
        (user?.isFranchise && user?.isNonFranchise
          ? x.officeType !== ""
          : user?.isFranchise
          ? x.officeType?.toLowerCase() === "franchise"
          : x.officeType?.toLowerCase() !== "franchise")
    )
    .map((x) => {
      return {
        id: x.officeCode,
        value: x.officeId,
        label: x.officeName,
      };
    });

  const sfOfficeCodeOpt = agencies
    ?.filter(
      (x) =>
        x.officeCode !== null &&
        x.officeCode !== undefined &&
        x.officeCode !== "" &&
        (user?.isFranchise && user?.isNonFranchise
          ? x.officeType !== ""
          : user?.isFranchise
          ? x.officeType?.toLowerCase() === "franchise"
          : x.officeType?.toLowerCase() !== "franchise")
    )
    ?.map((x) => {
      return {
        name: x.businessOperation,
        id: x.officeName,
        value: x.officeCode,
        label: x.officeCode,
        phone: x.phone,
      };
    });

  const sfStoreNumberOpt = agencies
    ?.filter(
      (x) =>
        x.storeNumber !== null &&
        x.storeNumber !== undefined &&
        x.storeNumber !== "" &&
        (user?.isFranchise && user?.isNonFranchise
          ? x.officeType !== ""
          : user?.isFranchise
          ? x.officeType?.toLowerCase() === "franchise"
          : x.officeType?.toLowerCase() !== "franchise")
    )
    .map((x) => {
      return {
        id: x.officeCode,
        value: x.storeNumber,
        label: x.storeNumber,
      };
    });

  const sfCityOpt = agencies
    ?.filter(
      (x) =>
        x.storeNumber !== null &&
        x.storeNumber !== undefined &&
        x.storeNumber !== "" &&
        (user?.isFranchise && user?.isNonFranchise
          ? x.officeType !== ""
          : user?.isFranchise
          ? x.officeType?.toLowerCase() === "franchise"
          : x.officeType?.toLowerCase() !== "franchise")
    )
    .map((x) => {
      return {
        id: x.officeCode,
        value: x.city,
        label: x.city,
      };
    });

  const { data: officeCities, isFetching: officeCitiesFetch } =
    useOfficeCities();

  const officeCitiesOpt = officeCities?.map((y) => {
    return {
      label: y.city,
      value: y.city,
    };
  });

  const { data: producerCodes, isFetching: prodCodeFetch } = useProducerCodes();

  const producerCodesOpt = producerCodes?.map((y) => {
    return {
      label: y.producerCode,
      value: y.producerCode,
    };
  });

  const { data: officePhones, isFetching: officePhonesFetch } =
    useOfficePhones();

  const officePhonesOpt = officePhones?.map((y) => {
    return {
      label: y.phone,
      value: y.phone,
    };
  });

  const officeDistrictCodeOpts = Array.from(
    new Map(
      officeDistrictCodes?.map((x) => [
        x.districtCode,
        { label: x.districtCode, value: x.districtCode },
      ])
    ).values()
  );

  const officeBrandMapper = (e) => {
    return e?.map((x) => x.value);
  }

  return (
    <div className="pr-3 space-y-2 pt-2">
      <div className="pt-2">
        <BarsArrowUpIcon className="h-6 w-6 text-primary mb-1.5" />
        <div className="text-xl font-bold text-primary -mt-9 ml-8">
          Search Section
        </div>
      </div>
      <div className="w-full">
        <div className="mb-3">
          <Disclosure defaultOpen={filter}>
            {({ open }) => (
              <>
                <Disclosure.Button className="sm:px-2 flex justify-between w-full px-4 py-2 text-sm font-medium text-left text-primary bg-gray-100 rounded-t-lg hover:bg-gray-200 focus:outline-none focus-visible:ring focus-visible:ring-purple-500 focus-visible:ring-opacity-75">
                  <span>Office Details</span>
                  <ChevronDownIcon
                    className={`${
                      open ? "transform rotate-180" : ""
                    } w-5 h-5 text-gray-500`}
                  />
                </Disclosure.Button>
                <Disclosure.Panel className="bg-gray-100 text-sm text-primary rounded-b-lg">
                  <div className="bg-gray-100  p-3 sm:px-2 md:px-3">
                    <div className="text-xs text-secondary italic font-normal pb-4">
                      <b> Note:</b> All fields are multiple selection.
                    </div>
                    <div className="flex flex-col text-xs gap-x-4 gap-y-2">
                      <div className="w-full">
                        <label className="block font-medium text-gray-700 mb-2">
                          Office Code
                        </label>
                        {/* <TextInput
                          value={searchInputsData?.OfficeCode}
                          name="OfficeCode"
                          onChange={(e) => {
                            setSearchInputsData({
                              ...searchInputsData,
                              OfficeCode: e.target.value,
                            });
                          }}
                        /> */}
                        <SelectHookFormMulti
                          options={sfOfficeCodeOpt}
                          value={searchInputsData?.OfficeCode}
                          onChange={(e) => {
                            setSearchInputsData({
                              ...searchInputsData,
                              OfficeCodeId: e?.map((x) => x.value),
                              OfficeCode: e,
                            });
                          }}
                          isLoading={officeStatus?.isFetching}
                          secondDiv="items-center"
                        />
                      </div>
                      <div className="w-full">
                        <label className="block font-medium text-gray-700 mb-2">
                          Office Name
                        </label>
                        {/* <TextInput
                          name="OfficeName"
                          value={searchInputsData?.OfficeName}
                          onChange={(e) => {
                            setSearchInputsData({
                              ...searchInputsData,
                              OfficeName: e.target.value,
                            });
                          }}
                        /> */}
                        <SelectHookFormMulti
                          options={sfOfficeNameOpt}
                          value={searchInputsData?.OfficeName}
                          onChange={(e) => {
                            setSearchInputsData({
                              ...searchInputsData,
                              OfficeNameId: e?.map((x) => x.value),
                              OfficeName: e,
                            });
                          }}
                          isLoading={officeStatus?.isFetching}
                          secondDiv="items-center"
                        />
                      </div>
                      <div className="w-full">
                        <label className="block font-medium text-gray-700 mb-2">
                          Corporate Office #
                        </label>
                        {/* <TextInput
                          name="StoreNumber"
                          value={searchInputsData?.StoreNumber}
                          onChange={(e) => {
                            setSearchInputsData({
                              ...searchInputsData,
                              StoreNumber: e.target.value,
                            });
                          }}
                        /> */}
                        <SelectHookFormMulti
                          options={sfStoreNumberOpt}
                          value={searchInputsData?.StoreNumber}
                          onChange={(e) => {
                            setSearchInputsData({
                              ...searchInputsData,
                              OfficeStoreNumberId: e?.map((x) => x.value),
                              StoreNumber: e,
                            });
                          }}
                          isLoading={officeStatus?.isFetching}
                          secondDiv="items-center"
                        />
                      </div>
                      <div className="w-full">
                        <label className="block font-medium text-gray-700 mb-2">
                          Office Status
                        </label>

                        <SelectHookFormMulti
                          options={officeStatusOpt}
                          value={searchInputsData?.SelectedOfficeStatus}
                          onChange={(e) => {
                            setSearchInputsData({
                              ...searchInputsData,
                              OfficeStatusId: e?.map((x) => x.value),
                              SelectedOfficeStatus: e,
                            });
                          }}
                          isLoading={officeStatus?.isFetching}
                          secondDiv="items-center"
                        />
                      </div>
                      <div className="w-full">
                        <label className="block font-medium text-gray-700 mb-2">
                          Office Type
                        </label>
                        <SelectHookFormMulti
                          options={officeTypeOpt}
                          value={searchInputsData?.SelectedOfficeType}
                          onChange={(e) => {
                            setSearchInputsData({
                              ...searchInputsData,
                              OfficeTypeId: e?.map((x) => x.value),
                              SelectedOfficeType: e,
                            });
                          }}
                          isLoading={officeTypeOpt?.isFetching}
                          secondDiv="items-center"
                        />
                      </div>
                      <div className="w-full">
                        <label className="block font-medium text-gray-700 mb-2">
                          District Code
                        </label>
                        <SelectHookFormMulti
                          options={officeDistrictCodeOpts}
                          value={searchInputsData?.SelectedDistrictCode}
                          onChange={(e) => {
                            setSearchInputsData({
                              ...searchInputsData,
                              DistrictCode: e?.map((x) => x.value),
                              SelectedDistrictCode: e,
                            });
                          }}
                          isLoading={officeIsLoading}
                          secondDiv="items-center"
                        />
                      </div>

                      <Disclosure>
                        {({ open }) => (
                          <>
                            <Disclosure.Button className="sm:px-2 flex justify-between w-full px-4 py-2 text-xs font-medium text-left text-primary bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus-visible:ring focus-visible:ring-purple-500 focus-visible:ring-opacity-75">
                              <span>More Filters</span>
                              <ChevronDownIcon
                                className={`${
                                  open ? "transform rotate-180" : ""
                                } w-4 h-4 text-gray-500`}
                              />
                            </Disclosure.Button>
                            <Disclosure.Panel className="bg-gray-100 text-sm text-primary rounded-b-lg">
                              <div className="flex flex-col text-xs gap-x-4 gap-y-2">
                                <div className="w-full">
                                  <label className="block font-medium text-gray-700 mb-2">
                                    Channel
                                  </label>
                                  <SelectHookFormMulti
                                    options={activeChannel}
                                    value={searchInputsData?.SelectedChannel}
                                    onChange={(e) => {
                                      setSearchInputsData({
                                        ...searchInputsData,
                                        ChannelId: e?.map((x) => x.value),
                                        SelectedChannel: e,
                                      });
                                    }}
                                    isLoading={isChannelLoading}
                                    secondDiv="items-center"
                                  />
                                </div>
                                <div className="w-full">
                                  <label className="block font-medium text-gray-700 mb-2">
                                    Office Brand
                                  </label>
                                  <SelectHookFormMulti
                                    options={activeOfficeBrand}
                                    value={searchInputsData?.BrandNameSelected}
                                    onChange={(e) => {
                                      setSearchInputsData({
                                        ...searchInputsData,
                                        BrandName: officeBrandMapper(e),
                                        BrandNameSelected: e,
                                      });
                                    }}
                                    isLoading={officeBLoading}
                                    secondDiv="items-center"
                                  />
                                </div>
                                <div className="w-full">
                                  <label className="block font-medium text-gray-700 mb-2">
                                    GL Branch
                                  </label>
                                  <SelectHookFormMulti
                                    options={glEntityOpt}
                                    value={searchInputsData?.SelectedGLBranch}
                                    onChange={(e) => {
                                      setSearchInputsData({
                                        ...searchInputsData,
                                        GLBranch: e?.map((x) => x.value),
                                        SelectedGLBranch: e,
                                      });
                                    }}
                                    isLoading={isGlEntitiesLoading}
                                    secondDiv="items-center"
                                  />
                                </div>
                                <div className="w-full">
                                  <label className="block font-medium text-gray-700 mb-2">
                                    Producer Code
                                  </label>
                                  <SelectHookFormMulti
                                    options={producerCodesOpt}
                                    value={
                                      searchInputsData?.SelectedProducerCode
                                    }
                                    onChange={(e) => {
                                      setSearchInputsData({
                                        ...searchInputsData,
                                        ProducerCode: e?.map((x) => x.value),
                                        SelectedProducerCode: e,
                                      });
                                    }}
                                    isLoading={prodCodeFetch}
                                    secondDiv="items-center"
                                  />
                                </div>

                                <div className="w-full">
                                  <label className="block font-medium text-gray-700 mb-2">
                                    City
                                  </label>
                                  <SelectHookFormMulti
                                    options={officeCitiesOpt}
                                    value={searchInputsData?.SelectedCity}
                                    onChange={(e) => {
                                      setSearchInputsData({
                                        ...searchInputsData,
                                        City: e?.map((x) => x.value),
                                        SelectedCity: e,
                                      });
                                    }}
                                    isLoading={officeCitiesFetch}
                                    secondDiv="items-center"
                                  />
                                </div>
                                <div className="w-full">
                                  <label className="block font-medium text-gray-700 mb-2">
                                    State
                                  </label>
                                  <SelectHookFormMulti
                                    options={states}
                                    value={searchInputsData?.SelectedState}
                                    onChange={(e) => {
                                      setSearchInputsData({
                                        ...searchInputsData,
                                        State: e?.map((x) => x.value),
                                        SelectedState: e,
                                      });
                                    }}
                                    isLoading={stateLoading}
                                    secondDiv="items-center"
                                  />
                                </div>
                                <div className="w-full">
                                  <label className="block font-medium text-gray-700 mb-2">
                                    Policy Management System
                                  </label>
                                  <SelectHookFormMulti
                                    options={activeTargetSystem}
                                    value={
                                      searchInputsData?.SelectedTargetSystem
                                    }
                                    onChange={(e) => {
                                      setSearchInputsData({
                                        ...searchInputsData,
                                        TargetSystem: e?.map((x) => x.value),
                                        SelectedTargetSystem: e,
                                      });
                                    }}
                                    // isLoading={officeStatus?.isFetching}
                                    secondDiv="items-center"
                                  />
                                </div>
                                <div className="w-full">
                                  <label className="block font-medium text-gray-700 mb-2">
                                    Carriers
                                  </label>
                                  <SelectHookFormMulti
                                    options={carriers}
                                    value={searchInputsData.SelectedCarrier}
                                    onChange={(e) => {
                                      const carrierIdList = e.map((x) => {
                                        return x.value;
                                      });
                                      setSearchInputsData({
                                        ...searchInputsData,
                                        Carriers: carrierIdList,
                                        SelectedCarrier: e,
                                      });
                                    }}
                                    // isLoading={officeStatus?.isFetching}
                                    secondDiv="items-center"
                                  />
                                </div>
                                <div className="w-full">
                                  <label className="block font-medium text-gray-700 mb-2">
                                    Phone Number
                                  </label>
                                  <SelectHookFormMulti
                                    options={officePhonesOpt}
                                    value={searchInputsData?.SelectedPhone}
                                    onChange={(e) => {
                                      setSearchInputsData({
                                        ...searchInputsData,
                                        Phone: e?.map((x) => x.value),
                                        SelectedPhone: e,
                                      });
                                    }}
                                    isLoading={officePhonesFetch}
                                    secondDiv="items-center"
                                  />
                                </div>
                              </div>
                            </Disclosure.Panel>
                          </>
                        )}
                      </Disclosure>

                      <div className="flex justify-center items-end gap-x-2 mb-12  lg:ml-4 md:ml-1 rounded-md mt-5">
                        <SearchButton
                          buttonValue="Search"
                          onClick={() => {
                            filterOfficeSettings();
                            setIsSearching(true);
                          }}
                        />
                        <RefreshButton
                          onClick={() => {
                            refreshOfficeTable();
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </Disclosure.Panel>
              </>
            )}
          </Disclosure>
        </div>
      </div>
    </div>
  );
};

export default TableFilters;
